import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useSlackCardPreview } from "./hooks/useSlackCardPreview";
import styles from "./SlackCardPreview.module.css";

// RenderCard (backend/notifications/card.go) escapes "&" first, then "<", then ">", so
// decoding reverses that order: "&lt;"/"&gt;" first (neither can produce a stray "&"),
// "&amp;" last. Decoding &amp; first would turn an escaped "&amp;lt;" into a literal "<"
// instead of the "&lt;" text the MIM typed.
const decodeMrkdwnEntities = (s) =>
  (s || "").replaceAll("&lt;", "<").replaceAll("&gt;", ">").replaceAll("&amp;", "&");

const SAFE_HREF = /^(https?:\/\/|\/)/i;

// Mirrors the Slack card the DM and the peer-review thread reply send (one renderer,
// RenderCard) so a MIM can see the card before sending it. Plain text only -- no
// dangerouslySetInnerHTML -- decoding only undoes RenderCard's own escaping for display.
function SlackCardPreview({ problemNumber, outageNumber, payload, onCardLoaded }) {
  const { card, loading, error, retry } = useSlackCardPreview(
    problemNumber,
    outageNumber,
    payload,
  );

  // Decoded, not the raw mrkdwn-escaped card.title: a caller seeding a free-text input from
  // this (the Slack/SMS Subject box) must get plain text back, or re-sending it unedited would
  // double-escape "&"/"<"/">" when RenderCard escapes it again.
  useEffect(() => {
    if (card?.title) {
      onCardLoaded?.(decodeMrkdwnEntities(card.title));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card]);

  const hasLink = !!card?.linkUrl && !!card?.linkLabel && SAFE_HREF.test(card.linkUrl);

  return (
    <div className={styles.container} data-testid="slack-card-preview">
      <h3 className={styles.heading}>Slack</h3>
      <p className={styles.description}>
        What subscribers see in Slack when this communication is sent.
      </p>

      {loading && (
        <div className={styles.loading} role="status">
          Loading Slack preview&hellip;
        </div>
      )}

      {!loading && error && (
        <div className={styles.error} role="alert">
          <span>{error}</span>
          <button type="button" className={styles.retryButton} onClick={retry}>
            Retry
          </button>
        </div>
      )}

      {!loading && !error && !card && (
        <p className={styles.empty}>No Slack preview available.</p>
      )}

      {!loading && !error && card && (
        <div className={styles.card}>
          {card.state && <div className={styles.line}>{decodeMrkdwnEntities(card.state)}</div>}
          <div className={styles.title}>{decodeMrkdwnEntities(card.title)}</div>
          <div className={styles.line}>{decodeMrkdwnEntities(card.body)}</div>
          {hasLink && (
            <a
              className={styles.link}
              href={card.linkUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {decodeMrkdwnEntities(card.linkLabel)}
            </a>
          )}
        </div>
      )}
    </div>
  );
}

SlackCardPreview.propTypes = {
  problemNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  outageNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  payload: PropTypes.object,
  onCardLoaded: PropTypes.func,
};

export default SlackCardPreview;
